/**
 * OfflineNoticeOverlay — Aviso proactivo sin internet (pantalla + banner).
 *
 * QUÉ HACE: Overlay blanco al perder internet; banner persistente en modo local.
 * QUÉ INVOCA: offlineNoticeStore, refreshDeviceOnlineStatus, LoadErrorScreen
 * QUÉ NO DEBE HACER: Bloquear la app para siempre ni borrar datos locales.
 */
import { useCallback, useEffect, useState } from "react";
import { WifiOff } from "lucide-react";
import toast from "react-hot-toast";
import LoadErrorScreen from "@/components/common/LoadErrorScreen";
import { refreshDeviceOnlineStatus } from "@/lib/networkStatus";
import { useOfflineNoticeStore } from "@/store/offlineNoticeStore";
import { STORAGE_QUOTA_EVENT } from "@/mocks/helpers/storage";

export function OfflineNoticeOverlay() {
  const online = useOfflineNoticeStore((s) => s.online);
  const dismissed = useOfflineNoticeStore((s) => s.dismissed);
  const setOnline = useOfflineNoticeStore((s) => s.setOnline);
  const dismissForSession = useOfflineNoticeStore((s) => s.dismissForSession);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const handleQuota = () => {
      toast.error(
        "Memoria local llena: esta operación NO se guardó. Descarga un respaldo y libera espacio en Ajustes → Almacenamiento.",
        { duration: 10000, id: "storage-quota" },
      );
    };
    window.addEventListener(STORAGE_QUOTA_EVENT, handleQuota);
    return () => window.removeEventListener(STORAGE_QUOTA_EVENT, handleQuota);
  }, []);

  const handleRetry = useCallback(async () => {
    setChecking(true);
    try {
      const ok = await refreshDeviceOnlineStatus();
      setOnline(ok);
    } finally {
      setChecking(false);
    }
  }, [setOnline]);

  if (online) return null;

  if (!dismissed) {
    return (
      <LoadErrorScreen
        variant="app"
        title="Sin conexión a internet"
        message={
          "Revisa Wi‑Fi o datos móviles.\n\nPuedes continuar en modo local: las ventas y movimientos se guardan en este dispositivo.\n\nImpresora Wi‑Fi: usa la red local del negocio (no requiere internet). Bluetooth y USB funcionan sin internet."
        }
        buttonLabel={checking ? "Comprobando…" : "Reintentar conexión"}
        secondaryLabel="Continuar sin internet"
        onSecondary={dismissForSession}
        onRetry={() => {
          if (!checking) void handleRetry();
        }}
        footNote="Tus datos locales no se borran al continuar."
      />
    );
  }

  return (
    <div
      className="fixed left-0 right-0 z-[400] flex items-center justify-center gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2 text-center dark:border-amber-900/50 dark:bg-amber-950/90"
      style={{ top: "var(--safe-area-top, 0px)" }}
      role="status"
    >
      <WifiOff className="h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300" />
      <p className="text-[11px] font-semibold leading-snug text-amber-900 dark:text-amber-100 sm:text-xs">
        Sin internet — modo local. Ventas guardadas en este dispositivo.
      </p>
      <button
        type="button"
        onClick={() => void handleRetry()}
        disabled={checking}
        className="ml-1 shrink-0 rounded-lg border border-amber-300 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-800 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-700 dark:bg-amber-900 dark:text-amber-100"
      >
        Reintentar
      </button>
    </div>
  );
}

export default OfflineNoticeOverlay;
